/**
 * Debug-ID discovery: bundler plugins (Sentry's and the emerging standard)
 * inject a global map of stack-trace snippets → debug IDs into every built
 * chunk. Resolving those to filenames lets the backend pick the exact source
 * map for a frame with no release/dist coordination.
 *
 * Adapted from sentry-javascript (packages/core/src/utils/debug-ids.ts),
 * MIT License, Copyright (c) 2019-present Functional Software, Inc. dba Sentry.
 *
 * Covered by __tests__/debug-ids.test.ts.
 */

import { parseStack, StackFrame } from './stack-parser';

type DebugIdContainer = {
    _debugIds?: Record<string, string>;
    _sentryDebugIds?: Record<string, string>;
};

interface CacheEntry {
    map: Record<string, string>;
    debugIdKeyCount: number;
    sentryDebugIdKeyCount: number;
}

let cache: CacheEntry | undefined;

/** Test hook: reset the memoized filename→debugId map. */
export function resetDebugIdCache(): void {
    cache = undefined;
}

function buildFilenameMap(byStackKey: Record<string, string>, into: Record<string, string>): void {
    for (const stackKey of Object.keys(byStackKey)) {
        const frames = parseStack(stackKey);
        for (let i = frames.length - 1; i >= 0; i -= 1) {
            const file = frames[i]?.file;
            if (file) {
                into[file] = byStackKey[stackKey];
                break;
            }
        }
    }
}

/**
 * The current filename → debug-ID map, derived from the global registries.
 * Memoized on registry sizes (chunks load over time, so the map can grow).
 */
export function getFilenameDebugIdMap(): Record<string, string> {
    const g = globalThis as DebugIdContainer;
    const debugIds = g._debugIds;
    const sentryDebugIds = g._sentryDebugIds;
    if (!debugIds && !sentryDebugIds) {
        return {};
    }

    const debugIdKeyCount = debugIds ? Object.keys(debugIds).length : 0;
    const sentryDebugIdKeyCount = sentryDebugIds ? Object.keys(sentryDebugIds).length : 0;
    if (cache && cache.debugIdKeyCount === debugIdKeyCount && cache.sentryDebugIdKeyCount === sentryDebugIdKeyCount) {
        return cache.map;
    }

    const map: Record<string, string> = {};
    if (sentryDebugIds) {
        buildFilenameMap(sentryDebugIds, map);
    }
    // Native _debugIds wins over _sentryDebugIds for the same file.
    if (debugIds) {
        buildFilenameMap(debugIds, map);
    }
    cache = { map, debugIdKeyCount, sentryDebugIdKeyCount };
    return map;
}

/**
 * The subset of the debug-ID map covering the files in a report's frames.
 * Returns undefined when nothing matches, so reports stay unchanged on apps
 * without a debug-ID-injecting bundler plugin.
 */
export function getDebugIdMapForFrames(frames: StackFrame[]): Record<string, string> | undefined {
    const full = getFilenameDebugIdMap();
    let out: Record<string, string> | undefined;
    for (const frame of frames) {
        const file = frame.file;
        if (file && full[file]) {
            if (!out) {
                out = {};
            }
            out[file] = full[file];
        }
    }
    return out;
}
